/**
 * Carrinho de compra ÚNICO do app (doc D item 5 / FFINAL Onda 2 antecipada).
 *
 * Antes `usePurchaseCart` era um hook simples: Sellout, Purchasing e
 * /operacoes tinham CADA UM sua instância de estado, "sincronizadas" só por
 * localStorage (last-writer-wins, sem re-render cruzado) → carrinho divergente
 * entre páginas. Agora o estado vive AQUI (single source of truth) e
 * `usePurchaseCart()` — mesma assinatura pública de sempre — lê/escreve o
 * contexto. localStorage continua com a MESMA key/shape (`purchase_order_cart_v1`),
 * então carrinhos existentes são preservados.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const STORAGE_KEY = 'purchase_order_cart_v1';

export const CartContext = createContext(null);

function normalizeSkuKey(value) {
    const normalized = String(value || '')
        .trim()
        .toUpperCase();
    return normalized || null;
}

function toOptionalNumber(value) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
}

function normalizeItemId(value) {
    const normalized = String(value || '')
        .trim()
        .toUpperCase();
    return normalized || null;
}

function getCartItemKey(item = {}) {
    const sku = normalizeSkuKey(item.sku);
    if (sku) {
        return `sku:${sku}`;
    }

    const itemId = normalizeItemId(item.item_id);
    if (itemId) {
        return `item:${itemId}`;
    }

    return null;
}

function readStoredCart() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) {
            return [];
        }

        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) {
            return [];
        }

        return parsed
            .map((entry) => ({
                key: String(entry?.key || '').trim(),
                sku: entry?.sku || null,
                item_id: entry?.item_id || null,
                item_ids: Array.isArray(entry?.item_ids)
                    ? entry.item_ids.map((value) => String(value || '').trim()).filter(Boolean)
                    : entry?.item_id
                      ? [String(entry.item_id).trim()]
                      : [],
                inventory_id: entry?.inventory_id || null,
                title: entry?.title || entry?.product_name || 'Produto',
                product_name: entry?.product_name || entry?.title || 'Produto',
                bling_stock: toOptionalNumber(entry?.bling_stock),
                cost_price: toOptionalNumber(entry?.cost_price),
                quantity: Math.max(1, Number.parseInt(String(entry?.quantity || 0), 10) || 1),
            }))
            .filter((entry) => entry.key);
    } catch {
        return [];
    }
}

export const CartProvider = ({ children }) => {
    const [items, setItems] = useState(() => readStoredCart());

    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
        } catch {
            // ignore localStorage write errors
        }
    }, [items]);

    const addItem = useCallback((item) => {
        const key = getCartItemKey(item);
        const quantity = Math.floor(Number(item?.quantity || 0));
        if (!key || !Number.isFinite(quantity) || quantity <= 0) {
            return false;
        }

        setItems((current) => {
            const index = current.findIndex((entry) => entry.key === key);
            if (index < 0) {
                return [
                    ...current,
                    {
                        key,
                        sku: item.sku || null,
                        item_id: item.item_id || null,
                        item_ids: Array.isArray(item.item_ids)
                            ? item.item_ids.map((value) => String(value || '').trim()).filter(Boolean)
                            : item.item_id
                              ? [String(item.item_id).trim()]
                              : [],
                        inventory_id: item.inventory_id || null,
                        title: item.title || item.product_name || 'Produto',
                        product_name: item.product_name || item.title || 'Produto',
                        bling_stock: toOptionalNumber(item.bling_stock),
                        cost_price: toOptionalNumber(item.cost_price),
                        quantity,
                    },
                ];
            }

            return current.map((entry, entryIndex) =>
                entryIndex === index
                    ? {
                          ...entry,
                          title: item.title || item.product_name || entry.title,
                          product_name: item.product_name || item.title || entry.product_name || entry.title,
                          item_ids: Array.from(
                              new Set([
                                  ...(Array.isArray(entry.item_ids) ? entry.item_ids : entry.item_id ? [entry.item_id] : []),
                                  ...(Array.isArray(item.item_ids) ? item.item_ids : item.item_id ? [item.item_id] : []),
                              ]),
                          ),
                          bling_stock: toOptionalNumber(item.bling_stock) ?? entry.bling_stock ?? null,
                          cost_price: toOptionalNumber(item.cost_price) ?? entry.cost_price ?? null,
                          quantity: entry.quantity + quantity,
                      }
                    : entry,
            );
        });

        return true;
    }, []);

    const updateItemQuantity = useCallback((key, quantity) => {
        const normalizedQuantity = Math.floor(Number(quantity || 0));
        if (!Number.isFinite(normalizedQuantity) || normalizedQuantity <= 0) {
            setItems((current) => current.filter((entry) => entry.key !== key));
            return;
        }

        setItems((current) =>
            current.map((entry) =>
                entry.key === key
                    ? {
                          ...entry,
                          quantity: normalizedQuantity,
                      }
                    : entry,
            ),
        );
    }, []);

    const removeItem = useCallback((key) => {
        setItems((current) => current.filter((entry) => entry.key !== key));
    }, []);

    const clear = useCallback(() => {
        setItems([]);
    }, []);

    const summary = useMemo(() => {
        const totalItems = items.length;
        const totalUnits = items.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
        const estimatedCost = items.reduce((sum, item) => {
            const costPrice = Number(item.cost_price || 0);
            const quantity = Number(item.quantity || 0);
            return sum + (Number.isFinite(costPrice) ? costPrice * quantity : 0);
        }, 0);
        return {
            totalItems,
            totalUnits,
            estimatedCost,
        };
    }, [items]);

    const payloadItems = useMemo(
        () =>
            items.map((item) => ({
                sku: item.sku || undefined,
                item_id: item.item_id || undefined,
                quantity: Number(item.quantity || 0),
                unit_cost: item.cost_price ?? undefined,
            })),
        [items],
    );

    const value = useMemo(
        () => ({
            items,
            summary,
            addItem,
            updateItemQuantity,
            removeItem,
            clear,
            payloadItems,
        }),
        [items, summary, addItem, updateItemQuantity, removeItem, clear, payloadItems],
    );

    return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
    const context = useContext(CartContext);
    if (!context) {
        throw new Error('useCart deve ser usado dentro de CartProvider');
    }
    return context;
};
